import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format } from 'date-fns'
import {
  apiMasterGetAgent,
  apiMasterGetAgentKenoLimits,
  apiMasterPutAgentKenoLimits,
  apiMasterGetAgentMembers,
  apiMasterUpdateAgent,
  apiMasterSetAgentStatus,
  apiAgentGetProfile,
  ApiRequestError,
  type AdminPartner,
  type AdminMember,
} from '@/lib/api'
import type { PaginatedResult } from '@nexbit/types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { PasswordInput } from '@/components/PasswordInput'
import { AgentLoginCard } from '@/components/AgentLoginCard'
import { KenoLimitPicker } from '@/components/keno/KenoLimitPicker'
import { cn } from '@/lib/utils'
import {
  ArrowLeft,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Save,
  ShieldOff,
  ShieldCheck,
  Loader2,
  Lock,
  CheckCircle2,
} from 'lucide-react'
import { useT } from '@/contexts/language'

const editSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  // agent share as a whole-percent string chosen from a select (0..cap)
  commissionRate: z.string().regex(/^\d{1,3}$/, 'Pick a share'),
  walletType: z.enum(['internal', 'seamless']),
  walletUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  // Write-only: the stored key never comes back, so blank means "keep the current one".
  walletApiKey: z.string().max(255).optional(),
})
type EditForm = z.infer<typeof editSchema>

function statusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (s === 'active') return 'success'
  if (s === 'suspended') return 'destructive'
  if (s === 'inactive') return 'warning'
  return 'outline'
}

/** A seamless agent's members cannot bet until both halves of its wallet API are set. */
function walletIncomplete(agent: AdminPartner): boolean {
  return agent.walletType === 'seamless' && (!agent.walletUrl || !agent.walletApiKeyMasked)
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p className="font-mono text-xs text-destructive flex items-center gap-1" role="alert">
      <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
      {message}
    </p>
  )
}

export default function MasterAgentDetailPage() {
  const t = useT()
  const navigate = useNavigate()
  const { agentId } = useParams<{ agentId: string }>()
  const loadKenoLimits = useCallback(() => apiMasterGetAgentKenoLimits(agentId ?? ''), [agentId])
  const saveKenoLimits = useCallback((ids: string[]) => apiMasterPutAgentKenoLimits(agentId ?? '', ids), [agentId])

  const [agent, setAgent] = useState<AdminPartner | null>(null)
  const [members, setMembers] = useState<PaginatedResult<AdminMember> | null>(null)
  // The master's own agent_share_rate is the ceiling it can hand out. Whole percent.
  const [capPct, setCapPct] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [saved, setSaved] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<EditForm>({ resolver: zodResolver(editSchema) })
  const walletType = watch('walletType')
  const agentPct = parseInt(watch('commissionRate') || '0', 10)
  const masterPct = Math.max(0, capPct - agentPct) // derived, display-only
  const housePct = Math.max(0, 100 - capPct) // the fixed cut the house keeps

  const load = useCallback(async () => {
    if (!agentId) return
    setLoading(true)
    setError(null)
    try {
      const [a, m, me] = await Promise.all([
        apiMasterGetAgent(agentId),
        apiMasterGetAgentMembers(agentId, 1, 50),
        apiAgentGetProfile(), // the calling master; commissionRate = its cap
      ])
      const cap = Math.round(parseFloat(me.commissionRate) * 100)
      setAgent(a)
      setMembers(m)
      setCapPct(cap)
      // Clamp an out-of-range legacy value down to the cap so the select can show it.
      const agentShare = Math.min(Math.round(parseFloat(a.commissionRate) * 100), cap)
      reset({
        name: a.name,
        commissionRate: String(agentShare),
        walletType: a.walletType === 'seamless' ? 'seamless' : 'internal',
        walletUrl: a.walletUrl ?? '',
        walletApiKey: '',
      })
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [agentId, reset, t.common.error])

  useEffect(() => {
    void load()
  }, [load])

  async function onSave(values: EditForm) {
    if (!agentId) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await apiMasterUpdateAgent(agentId, {
        name: values.name,
        commissionRate: (parseInt(values.commissionRate, 10) / 100).toFixed(4),
        walletType: values.walletType,
        walletUrl: values.walletUrl || undefined,
        walletApiKey: values.walletApiKey || undefined,
      })
      // The update reply comes from a query that does not read the key back, so keep the
      // "is one set" knowledge the page already had unless a new key was just sent.
      setAgent((prev) => ({
        ...updated,
        loginUsername: prev?.loginUsername ?? null,
        walletApiKeyMasked: values.walletApiKey ? '••••' : (prev?.walletApiKeyMasked ?? ''),
      }))
      reset({ ...values, walletApiKey: '' })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setSaving(false)
    }
  }

  async function toggleStatus() {
    if (!agent || !agentId) return
    setTogglingStatus(true)
    setError(null)
    try {
      const next = agent.status === 'active' ? 'suspended' : 'active'
      const updated = await apiMasterSetAgentStatus(agentId, next)
      setAgent((prev) => ({
        ...updated,
        loginUsername: prev?.loginUsername ?? null,
        walletApiKeyMasked: prev?.walletApiKeyMasked ?? '',
      }))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setTogglingStatus(false)
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/master/agents')}
          className="h-8 gap-1.5 font-mono text-xs shrink-0"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          เอเจนต์
        </Button>
        <div className="min-w-0 flex-1">
          {loading || !agent ? (
            <Skeleton className="h-5 w-40" />
          ) : (
            <div className="flex items-center gap-2.5">
              <h1 className="font-mono text-sm font-bold uppercase tracking-wider text-foreground truncate">
                {agent.name}
              </h1>
              <Badge variant={statusVariant(agent.status)} className="text-[10px] shrink-0">
                {agent.status}
              </Badge>
              <span className="font-mono text-xs text-muted-foreground truncate">{agent.code}</span>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void load()}
          disabled={loading}
          className="h-8 gap-1.5 font-mono text-xs shrink-0"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {t.common.refresh}
        </Button>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-56 w-full rounded-[var(--radius)]" />
          <Skeleton className="h-40 w-full rounded-[var(--radius)]" />
          <Skeleton className="h-64 w-full rounded-[var(--radius)]" />
        </div>
      ) : !agent || !agentId ? (
        <div className="py-12 text-center border border-border rounded-[var(--radius)] bg-card">
          <p className="font-mono text-sm text-muted-foreground">ไม่พบเอเจนต์นี้</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* ── Edit agent ─────────────────────────────────────────────────── */}
          <form
            onSubmit={handleSubmit(onSave)}
            className="rounded-[var(--radius)] border border-border bg-card overflow-hidden shadow-[var(--shadow-sm)]"
            noValidate
          >
            <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                ข้อมูลเอเจนต์
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">
                เพดานหุ้นของคุณ {capPct}%
              </span>
            </div>

            <div className="p-4 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name" className="font-mono text-xs">
                  ชื่อเอเจนต์
                </Label>
                <Input
                  id="name"
                  className="font-mono text-sm"
                  aria-invalid={!!errors.name}
                  {...register('name')}
                />
                <FieldError message={errors.name?.message} />
              </div>

              {/* Share split — agent is chosen; master is the remainder of the cap */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="commissionRate" className="font-mono text-xs">
                    หุ้นเอเจนต์ (%)
                  </Label>
                  <select
                    id="commissionRate"
                    className={cn(
                      'h-9 rounded-[var(--radius-sm)] border border-input bg-background px-3 font-mono text-sm',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                    {...register('commissionRate')}
                  >
                    {Array.from({ length: capPct + 1 }, (_, i) => (
                      <option key={i} value={String(i)}>
                        {i}%
                      </option>
                    ))}
                  </select>
                  <FieldError message={errors.commissionRate?.message} />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="font-mono text-xs text-muted-foreground">
                    หุ้นมาสเตอร์ (%)
                    <span className="ml-1.5">คำนวณอัตโนมัติ</span>
                  </Label>
                  <div className="h-9 flex items-center gap-2 rounded-[var(--radius-sm)] border border-dashed border-border bg-muted/40 px-3">
                    <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-mono text-sm font-semibold text-foreground">{masterPct}%</span>
                    <span className="font-mono text-[10px] text-muted-foreground ml-auto">
                      {capPct}% − {agentPct}%
                    </span>
                  </div>
                </div>
              </div>

              <p className="font-mono text-[10px] text-muted-foreground">
                เอเจนต์ {agentPct}% · มาสเตอร์ {masterPct}% · บริษัท {housePct}% (คงที่)
              </p>

              {walletIncomplete(agent) && (
                <div className="flex items-start gap-2 p-3 rounded-[var(--radius-sm)] bg-warning/20" role="status">
                  <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="font-mono text-xs text-foreground">{t.agents.walletNotSet}</p>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <Label className="font-mono text-xs">{t.agents.walletType}</Label>
                <div className="flex rounded-[var(--radius-sm)] border border-border overflow-hidden max-w-xs">
                  {(['seamless', 'internal'] as const).map((wt) => (
                    <label
                      key={wt}
                      className={cn(
                        'flex-1 flex items-center justify-center py-2 font-mono text-xs cursor-pointer transition-colors',
                        walletType === wt
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card text-muted-foreground hover:bg-secondary',
                      )}
                    >
                      <input type="radio" value={wt} className="sr-only" {...register('walletType')} />
                      {wt}
                    </label>
                  ))}
                </div>
              </div>

              {walletType === 'seamless' && (
                <>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="walletUrl" className="font-mono text-xs">
                      {t.agents.walletUrl}
                    </Label>
                    <Input
                      id="walletUrl"
                      type="url"
                      placeholder="https://agent.example.com"
                      className="font-mono text-sm"
                      aria-invalid={!!errors.walletUrl}
                      {...register('walletUrl')}
                    />
                    <FieldError message={errors.walletUrl?.message} />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="walletApiKey" className="font-mono text-xs">
                      {t.agents.walletApiKey}
                      <span className="text-muted-foreground ml-1.5">({t.agents.walletApiKeyHint})</span>
                    </Label>
                    <PasswordInput
                      id="walletApiKey"
                      autoComplete="off"
                      placeholder={
                        agent.walletApiKeyMasked
                          ? t.agents.walletApiKeySet(agent.walletApiKeyMasked)
                          : 'agent-secret-key'
                      }
                      className="font-mono text-sm"
                      aria-invalid={!!errors.walletApiKey}
                      showLabel="Show key"
                      hideLabel="Hide key"
                      {...register('walletApiKey')}
                    />
                    <FieldError message={errors.walletApiKey?.message} />
                    {agent.walletApiKeyMasked && (
                      <p className="font-mono text-[10px] text-muted-foreground">{t.agents.walletApiKeyKeep}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void toggleStatus()}
                disabled={togglingStatus}
                className={cn(
                  'h-8 gap-1.5 font-mono text-xs',
                  agent.status === 'active'
                    ? 'text-destructive hover:text-destructive hover:bg-destructive/10'
                    : 'text-success hover:text-success hover:bg-success/10',
                )}
              >
                {togglingStatus ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : agent.status === 'active' ? (
                  <ShieldOff className="h-3.5 w-3.5" />
                ) : (
                  <ShieldCheck className="h-3.5 w-3.5" />
                )}
                {agent.status === 'active' ? 'ระงับเอเจนต์' : 'เปิดใช้งาน'}
              </Button>

              <div className="flex items-center gap-2">
                {saved && (
                  <span className="font-mono text-xs text-success flex items-center gap-1" role="status">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    บันทึกแล้ว
                  </span>
                )}
                <Button
                  type="submit"
                  size="sm"
                  disabled={saving || !isDirty}
                  className="h-8 gap-1.5 font-mono text-xs font-semibold uppercase tracking-wider"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  บันทึก
                </Button>
              </div>
            </div>
          </form>

          {/* ── Login account ──────────────────────────────────────────────── */}
          <AgentLoginCard agentId={agentId} username={agent.loginUsername ?? null} />

          {/* ── KENO bet limits: which rows this agent's members can pick ──── */}
          <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden shadow-[var(--shadow-sm)]">
            <div className="px-4 py-2.5 bg-secondary">
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                {t.kenoLimits.agentCardTitle}
              </span>
            </div>
            <div className="p-4 flex flex-col gap-3">
              <p className="text-sm text-muted-foreground max-w-prose">{t.kenoLimits.agentCardHint}</p>
              <KenoLimitPicker
                load={loadKenoLimits}
                save={saveKenoLimits}
                canEdit
              />
            </div>
          </div>

          {/* ── Members (read-only) ────────────────────────────────────────── */}
          <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden shadow-[var(--shadow-sm)]">
            <div className="px-4 py-2.5 bg-secondary flex items-center justify-between gap-2">
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                สมาชิกของเอเจนต์นี้
              </span>
              <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
                <Lock className="h-3 w-3" />
                ดูอย่างเดียว
                {members && ` · ${members.total} รายการ`}
              </span>
            </div>

            {!members || members.items.length === 0 ? (
              <div className="py-12 text-center">
                <p className="font-mono text-sm text-muted-foreground">{t.members.noMembers}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.common.username}</TableHead>
                    <TableHead>{t.common.status}</TableHead>
                    <TableHead className="text-right">{t.common.createdShort}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.items.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <p className="font-mono text-sm font-semibold">{m.username}</p>
                        {m.displayName && (
                          <p className="font-mono text-xs text-muted-foreground">{m.displayName}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(m.status)} className="text-[10px]">
                          {m.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <time
                          dateTime={m.createdAt}
                          className="font-mono text-xs text-muted-foreground"
                        >
                          {format(new Date(m.createdAt), 'dd/MM/yy')}
                        </time>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
